import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { RequestService } from '@shared/services/request/request.service';
import { DatasetCustomParams, DatasetRequestParams } from '@shared/services/request/request.types';
import { IChannel } from '@main/interfaces';
import { IEntityItemInstanceId } from '@main/types';
import { ReportConfig } from '@shared/interfaces/ReportConfig';
import { REPORT_CONFIG } from '@shared/const/report-config';
import { FiltersValue } from '@shared/services/filters/filters.types';
import { EntityHelpers } from '@main/helpers/entity.helpers';
import { SerializeEntitiesIdsOptions } from '@main/helpers/entity.helpers.types';
import { BUSINESS_ENTITY, PUBLISHER_TYPE, WIDGET } from '../enums';
import { EntityItem } from '../models';

export interface GetAvailablePublishersResponse {
    elements: AvailablePublisherItem[];
    impressions: {
        total: number;
    };
    count: {
        total: number;
    };
}

export interface AvailablePublisherItem {
    id: IEntityItemInstanceId;
    name: string;
    homePageUrl: string;
    favIconUrl: string;
    type: {
        code: string;
        title: string;
    };
    impressions: {
        total: number;
    };
    channels: {
        elements: IChannel[];
    };
}

@Injectable()
export class PublishersService {
    private getPublisherTypeData = {
        result: undefined,
        params: undefined,
    };

    constructor(
        private requestService: RequestService,
        @Inject(REPORT_CONFIG) protected reportConfig: ReportConfig,
    ) {}

    getAvailablePublishers({
        entities,
        filters,
        type,
        searchQuery,
        page,
        size,
    }: {
        entities: EntityItem[];
        filters: FiltersValue;
        type?: PUBLISHER_TYPE;
        searchQuery?: string;
        page?: number;
        size?: number;
    }): Observable<AvailablePublisherItem[]> {
        /* Keywords are queried by the keyword strings themselves, so they can not go through the entities pairs
         * serialization (it would key them by the campaign id). The options must stay identical either way,
         * otherwise grouped keywords are sent as group ids instead of the keywords they hold. */
        const serializeOptions: Partial<SerializeEntitiesIdsOptions> = {
            globalParentheses: true,
            extractGroupsIds: true,
            plain: true,
        };

        const isKeywords = this.reportConfig.entityType === BUSINESS_ENTITY.Keyword;
        const serializedKeywords = isKeywords ? EntityHelpers.serializeEntitiesIds(entities, serializeOptions) : null;

        /* Nothing to scope the publishers by: an empty keyword param would query every keyword instead of the
         * selected ones, so the request is skipped entirely. */
        if (isKeywords && !serializedKeywords) {
            return of([]);
        }

        const entitiesIdsPairs = isKeywords
            ? { keyword: serializedKeywords }
            : EntityHelpers.serializeEntitiesIdsPairs(entities, serializeOptions);

        const customParams: DatasetCustomParams = EntityHelpers.serializeFiltersParams(filters);

        if (type) {
            customParams.type = {
                operation: 'in',
                items: [type],
            };
        }

        if (searchQuery) {
            customParams.name = `contains(${searchQuery})`;
        }

        if (page) {
            customParams.page = page;
        }

        if (size) {
            customParams.size = size;
        }

        const datasetParams: DatasetRequestParams = {
            datasetName: 'available-publishers',
            entitiesIdsPairs,
            customParams,
            useViewToken: true,
            labelEntityType: BUSINESS_ENTITY.Advertiser,
            labelArea: WIDGET.TOP_ADS,
        };

        const cachedParams = JSON.stringify(datasetParams);

        if (this.getPublisherTypeData.result && cachedParams === this.getPublisherTypeData.params) {
            return of(JSON.parse(this.getPublisherTypeData.result));
        }

        return this.requestService.dataset<GetAvailablePublishersResponse>(datasetParams).pipe(
            map((response) => {
                const result = response?.elements || [];

                result[Symbol.for('total')] = response?.count.total || 0;

                return result;
            }),
        );
    }
}
